import { Grid } from "@material-ui/core";
import { useContext, useEffect, useMemo } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Observable } from "rxjs";
import { ErrorText } from "../../../../../../../components/billing";
import { FileField } from "../../../../../../../components/forms/file-3";
import { RadioGroup } from "../../../../../../../components/forms/radio";
import { GridItem } from "../../../../../../../components/layout";
import {
  AddressAttachmentType,
  useAddressAttachmentType,
} from "../../../../../../../store/common/models";
import { ColumnWrapper } from "../../../../../../../store/launch-configurations/models/default-fields/common";
import { mapObject } from "../../../../../../../utils/utils";
import { FileAttachmentContext } from "./file-attachment-context";
import { AddressTypeContext } from "./proof-of-address-type-context";
import { Container } from "../../components";
import { AttachmentFormType } from "../common/attachment";
import { useFileUploader } from "./file-upload-hook";

export interface IProofofAddressProps {
  emailChange: Observable<string>;
  setFilesUploaded: (b: boolean) => void;
}

export function ProofofAddress({
  emailChange,
  setFilesUploaded,
}: IProofofAddressProps) {
  const { t } = useTranslation("fields");
  const {
    formState: { isSubmitted: isParentFormSubmitted },
  } = useFormContext();

  const { files, setFile, emailAddress } = useContext(FileAttachmentContext);
  const { setAddressType, addressType } = useContext(AddressTypeContext);

  const [
    proofOfAddressSubject,
    proofOfAddressUploaded,
    proofOfAddressSuccess,
  ] = useFileUploader("PROOF_OF_ADDRESS", emailChange);

  useEffect(() => {
    const sub = proofOfAddressSuccess.subscribe((d) => {
      setFile("PROOF_OF_ADDRESS", d);
    });
    return () => sub.unsubscribe();
  }, [proofOfAddressSuccess, setFile]);

  const error = useMemo(
    () =>
      !proofOfAddressUploaded &&
      isParentFormSubmitted &&
      !files.PROOF_OF_ADDRESS
        ? new Error("Field is required")
        : undefined,
    [proofOfAddressUploaded, isParentFormSubmitted]
  );

  const {
    watch,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
  } = useForm<AttachmentFormType>({
    defaultValues: {
      type: addressType,
      file: files.PROOF_OF_ADDRESS?.file,
    },
  });

  const type = watch("type");
  const file = watch("file");

  useEffect(() => {
    setAddressType(type as AddressAttachmentType);
  }, [type, setAddressType]);

  const AddressFileType = useAddressAttachmentType();
  const typeOptions = useMemo(() => {
    return mapObject(AddressFileType.enum, (label, id) => ({
      id,
      label,
    }));
  }, [AddressFileType]);

  useEffect(() => {
    if (isParentFormSubmitted) trigger();
  }, [trigger, isParentFormSubmitted]);

  // Re-uploading the file if email has changed since last (to run only on mount, once)
  useEffect(() => {
    if (
      files?.PROOF_OF_ADDRESS?.emailAddress &&
      files?.PROOF_OF_ADDRESS?.emailAddress !== emailAddress
    ) {
      proofOfAddressSubject.next(files?.PROOF_OF_ADDRESS);
    }
  }, []);

  useEffect(() => {
    const sub = watch((data) => {
      proofOfAddressSubject.next(data);
      if (isParentFormSubmitted && !isSubmitted) {
        trigger();
      }
    });

    return () => sub.unsubscribe();
  }, [
    watch,
    isParentFormSubmitted,
    trigger,
    isSubmitted,
    proofOfAddressSubject,
  ]);

  useEffect(() => {
    if (proofOfAddressUploaded || files.PROOF_OF_ADDRESS) {
      setFilesUploaded(true);
    } else {
      setFilesUploaded(false);
    }
  }, [setFilesUploaded, proofOfAddressUploaded, files.PROOF_OF_ADDRESS]);

  return (
    <Container>
      <Grid container spacing={3}>
        <GridItem top xs={12}>
          <strong>{t("proof-of-address")}</strong>
        </GridItem>
        <GridItem top xs={12}>
          {t("choose-your-document-type")}
        </GridItem>
        <GridItem top xs={12}>
          <RadioGroup<AddressAttachmentType>
            fullWidth
            inline
            options={typeOptions}
            onChange={(e: string) => setValue(`type`, e as any)}
            value={(type as any) as AddressAttachmentType}
          />
        </GridItem>
        <GridItem top xs={12}>
          {t("upload-your-document")}
        </GridItem>
        <GridItem top xs={12}>
          <ColumnWrapper>
            <FileField
              onChange={(e: any) => setValue(`file`, e as any)}
              value={(file as any) as File}
              error={!!errors["file"]}
              helperText={errors["file"]?.message}
              label={t("address-attachment")}
            />
          </ColumnWrapper>
        </GridItem>
        <GridItem top xs={12}>
          <small>
            <em
              dangerouslySetInnerHTML={{
                __html: t("please-upload-the-document-clearly"),
              }}
            ></em>
          </small>
        </GridItem>
        {error ? (
          <GridItem top xs={12}>
            <ErrorText>{error?.message}</ErrorText>
          </GridItem>
        ) : null}
      </Grid>
    </Container>
  );
}
